import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { CommitAccepted, DetectedColumn, ImportAnalysis, ImportField, ImportType, ValidationOutcome } from '@raptor-fe/core';
import { ServiceUtils } from '../service-utils/service-utils';

/**
 * Raw analyze response shape as serialised by the API (`ImportAnalyzeOutputMessage`). Detected
 * columns arrive as bare header strings; the service wraps them for the mapping dialog.
 */
interface ImportAnalyzeResponse {
    importSessionId: string;
    detectedColumns: string[];
    targetFields: ImportFieldResponse[];
    suggestedMapping: Record<string, string>;
}

/** Raw import-type response shape (`ImportTypeOutputMessage`). Carries no icon. */
interface ImportTypeResponse {
    key: string;
    displayName: string;
    description: string;
    requiredPermissions: string[];
    targetFields: ImportFieldResponse[];
}

/** Raw import-field response shape (the nested `IImportField` element). `dataType` is the backend enum name. */
interface ImportFieldResponse {
    name: string;
    label: string;
    required: boolean;
    dataType: string;
    isList: boolean;
    instructions: string;
}

/**
 * Bootstrap icon per import type key. The API deliberately omits a per-type icon (it is a purely
 * presentational concern), so the client supplies one keyed on the stable type key, falling back to
 * a generic spreadsheet icon for any future type not listed here.
 */
const ICON_BY_TYPE_KEY: Readonly<Record<string, string>> = {
    parts: 'bi-box-seam',
    barcode: 'bi-upc-scan',
};

const DEFAULT_TYPE_ICON = 'bi-file-earmark-spreadsheet';

/**
 * HTTP client for the bulk Data Import endpoints (Spec #41297), rooted at
 * `api/v1/tenant/{tenantId}/import`. Lists the import types the caller may use, downloads a type's
 * template, analyzes an uploaded workbook, validates a confirmed mapping, and commits a validated
 * session as a background job. Progress is not polled here: commit returns the notification id the
 * existing notification/SignalR path reports progress on.
 */
@Injectable({
    providedIn: 'root',
})
export class ImportApiService {
    private readonly http = inject(HttpClient);
    private readonly serviceUtils = inject(ServiceUtils);

    /**
     * Lists the import types available to the caller. The API already filters to types whose full
     * required-permission set the caller holds. Each type is enriched with a client-derived icon and
     * its field data types normalised to lower-case.
     */
    getTypes(tenantId: string): Observable<ImportType[]> {
        return this.http
            .get<ImportTypeResponse[]>(this.serviceUtils.getBaseUrl('import', tenantId) + '/types')
            .pipe(map((types) => types.map((type) => this.mapType(type))));
    }

    /**
     * Downloads the `.xlsx` template for an import type as a binary blob for the browser to save.
     */
    downloadTemplate(tenantId: string, typeKey: string): Observable<Blob> {
        return this.http.get(this.serviceUtils.getBaseUrl('import', tenantId) + `/types/${typeKey}/template`, {
            responseType: 'blob',
        });
    }

    /**
     * Analyzes an uploaded workbook against an import type: stages the file, detects its columns, and
     * returns a suggested column-to-field mapping keyed to the staged session.
     */
    analyze(tenantId: string, typeKey: string, file: File): Observable<ImportAnalysis> {
        const form = new FormData();
        form.append('file', file, file.name);
        return this.http
            .post<ImportAnalyzeResponse>(this.serviceUtils.getBaseUrl('import', tenantId) + `/types/${typeKey}/analyze`, form)
            .pipe(map((response) => this.mapAnalysis(response)));
    }

    /**
     * Validates a staged session against a confirmed column-to-field mapping. The session becomes
     * committable only when the outcome reports no errors.
     */
    validate(tenantId: string, sessionId: string, columnToField: Record<string, string>): Observable<ValidationOutcome> {
        return this.http.post<ValidationOutcome>(this.serviceUtils.getBaseUrl('import', tenantId) + `/sessions/${sessionId}/validate`, {
            columnToField,
        });
    }

    /**
     * Commits a validated session, enqueuing a background import job. Returns the notification id the
     * import progress and terminal outcome are reported on. When `importValidRowsOnly` is true the
     * backend imports only the rows that passed validation and skips the rows with errors, rather than
     * rejecting the whole file.
     */
    commit(tenantId: string, sessionId: string, importValidRowsOnly = false): Observable<CommitAccepted> {
        return this.http.post<CommitAccepted>(this.serviceUtils.getBaseUrl('import', tenantId) + `/sessions/${sessionId}/commit`, {
            importValidRowsOnly,
        });
    }

    private mapType(type: ImportTypeResponse): ImportType {
        return {
            key: type.key,
            displayName: type.displayName,
            description: type.description,
            iconClass: ICON_BY_TYPE_KEY[type.key] ?? DEFAULT_TYPE_ICON,
            requiredPermissions: [...type.requiredPermissions],
            fields: type.targetFields.map((field) => this.mapField(field)),
        };
    }

    private mapAnalysis(response: ImportAnalyzeResponse): ImportAnalysis {
        return {
            importSessionId: response.importSessionId,
            detectedColumns: response.detectedColumns.map((name): DetectedColumn => ({ name })),
            targetFields: response.targetFields.map((field) => this.mapField(field)),
            suggestedMapping: { ...response.suggestedMapping },
        };
    }

    private mapField(field: ImportFieldResponse): ImportField {
        return {
            name: field.name,
            label: field.label,
            required: field.required,
            dataType: field.dataType.toLowerCase() as ImportField['dataType'],
            isList: field.isList,
            instructions: field.instructions,
        };
    }
}
